import { useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';

/**
 * Cadência do acompanhamento — a mesma do app (`pix_poller.dart`).
 *
 * 3s no primeiro minuto porque é quando a confirmação normalmente chega (19s no
 * teste de produção de 06/08/2026); 10s depois.
 */
const POLL_RAPIDO_MS = 3_000;
const POLL_LENTO_MS = 10_000;
const LIMIAR_LENTO_MS = 60_000;

/**
 * **Não reduzir.** São 15 minutos porque essa é a janela da conciliação do
 * servidor, não um palpite de quanto o pagamento demora: um teto menor faz a
 * tela desistir antes de o backend conciliar, justamente nos casos em que o
 * provedor não notifica — que já aconteceram.
 */
const TETO_MS = 15 * 60_000;

/** Um timeout isolado é ruído de rede; cinco seguidos são um problema real. */
const MAX_FALHAS_SEGUIDAS = 5;

/** Códigos em que insistir não adianta: a cobrança sumiu ou não é acessível. */
const CODIGOS_TERMINAIS = new Set([
  'PIX_MOTORISTA_INVALIDO', 'PIX_FORA_DA_HIERARQUIA',
  'PIX_COBRANCA_NAO_ENCONTRADA', 'PIX_FATURA_NAO_ELEGIVEL', 'PIX_MENSALIDADE_NAO_ELEGIVEL',
]);

/**
 * Estados em que ainda vale consultar.
 *
 * `CRIANDO` está aqui porque a cobrança é gravada ANTES da chamada ao provedor
 * (o INSERT é committado para que um timeout não perca o txid). Acompanhar só
 * `ATIVA` significa que, quando o provedor demora, o cooperado paga e a
 * confirmação nunca chega.
 */
const DEVE_POLLAR = new Set(['CRIANDO', 'ATIVA']);

/** Mensagem por código — nunca o texto cru do servidor na tela. */
const MENSAGENS: Record<string, string> = {
  PIX_MOTORISTA_INVALIDO: 'Sua sessão expirou. Entre novamente.',
  PIX_FORA_DA_HIERARQUIA: 'Esta cobrança não está disponível para você.',
  PIX_FATURA_NAO_ELEGIVEL: 'Esta fatura não está mais em aberto.',
  PIX_MENSALIDADE_NAO_ELEGIVEL: 'Esta mensalidade não está mais em aberto.',
  PIX_COBRANCA_NAO_ENCONTRADA: 'Não encontramos esta cobrança. Gere um novo código.',
  PIX_CHAVE_NAO_CONFIGURADA: 'Pagamento por Pix indisponível no momento. Fale com a cooperativa.',
  PIX_MTLS_FALHOU: 'Pagamento por Pix indisponível no momento. Fale com a cooperativa.',
  PIX_ESCOPO_NEGADO: 'Pagamento por Pix indisponível no momento. Fale com a cooperativa.',
  PIX_INDISPONIVEL: 'O banco está indisponível no momento. Tente em alguns minutos.',
  PIX_ERRO_INTERNO: 'Não foi possível gerar o Pix. Tente novamente.',
  CREDITO_COBRANCA_OUTRO_PACOTE: 'Você já tem uma cobrança aberta para outro pacote. Conclua ou cancele antes.',
};

export function mensagemDeErroPix(e: any): string {
  const codigo = e?.response?.data?.codigo;
  if (codigo && MENSAGENS[codigo]) return MENSAGENS[codigo];
  if (e?.code === 'ECONNABORTED') return 'Tempo esgotado. Tente novamente.';
  if (!e?.response) return 'Sem conexão. Verifique sua internet e tente novamente.';
  return 'Não foi possível gerar o Pix. Tente novamente.';
}

export interface PixCobrancaDto {
  txid: string;
  status: string;
  valor: number;
  vencimento?: string | null;
  pixCopiaECola?: string | null;
  qrCodePngBase64?: string | null;
  expiraEm?: string | null;
  pagoEm?: string | null;
  fatura?: { id: number; periodo: string; descricao: string } | null;
  /** 0105: a mensalidade que este Pix paga. */
  mensalidade?: { id: number; competencia: string; descricao: string } | null;
}

/**
 * O que está sendo pago — fatura ou recarga.
 *
 * O sheet não sabe qual: só muda quem cria a cobrança e o texto de contexto.
 * É a mesma generalização que o app fez em `pix_origem.dart`, e o motivo é o
 * mesmo: cobrança, acompanhamento e os desfechos são idênticos nos dois casos.
 */
export interface OrigemPix {
  /** Cria (ou recupera, se o servidor já tiver uma viva) a cobrança. */
  criar: () => Promise<PixCobrancaDto>;
  /** Consulta o estado atual. */
  consultar: (txid: string) => Promise<PixCobrancaDto>;
}

export function origemRecarga(idPacote: number): OrigemPix {
  return {
    criar: () => api.post<PixCobrancaDto>('/cooperado/creditos/recarga', { idPacote }).then(r => r.data),
    consultar: txid => api.get<PixCobrancaDto>(`/cooperado/creditos/pix/${txid}`).then(r => r.data),
  };
}

export function origemFatura(idFatura: number): OrigemPix {
  return {
    // Idempotente no servidor: cobrança viva é recuperada, não duplicada.
    criar: () => api.post<PixCobrancaDto>(`/cooperado/faturas/${idFatura}/cobranca`, {}).then(r => r.data),
    consultar: txid => api.get<PixCobrancaDto>(`/cooperado/faturas/pix/${txid}`).then(r => r.data),
  };
}

/** A mensalidade (0105). A consulta é a mesma rota das faturas: o servidor confere a origem. */
export function origemMensalidade(idMensalidade: number): OrigemPix {
  return {
    criar: () => api.post<PixCobrancaDto>(`/cooperado/mensalidades/${idMensalidade}/cobranca`, {}).then(r => r.data),
    consultar: txid => api.get<PixCobrancaDto>(`/cooperado/faturas/pix/${txid}`).then(r => r.data),
  };
}

/**
 * Cria a cobrança e acompanha até o desfecho.
 *
 * `statusEfetivo` blinda o caso do "ATIVA incompleto": uma cobrança marcada
 * ativa que ainda não trouxe o copia-e-cola é tratada como `CRIANDO`, e a tela
 * segue consultando — nunca exibe meia-tela de QR.
 */
export function usePixCobranca(origem: OrigemPix | null, onDesfecho?: () => void) {
  const [cobranca, setCobranca] = useState<PixCobrancaDto | null>(null);
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [tetoAtingido, setTetoAtingido] = useState(false);
  const [restanteMs, setRestanteMs] = useState(TETO_MS);

  const inicioRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const origemRef = useRef(origem);
  origemRef.current = origem;

  const statusEfetivo = cobranca
    ? (cobranca.status === 'ATIVA' && !cobranca.pixCopiaECola ? 'CRIANDO' : cobranca.status)
    : null;

  useEffect(() => {
    if (!origem) {
      setCobranca(null); setErro(null); setTetoAtingido(false); setRestanteMs(TETO_MS);
      return;
    }
    setCriando(true);
    setErro(null);
    origem.criar()
      .then(dto => { setCobranca(dto); inicioRef.current = Date.now(); })
      .catch(e => setErro(mensagemDeErroPix(e)))
      .finally(() => setCriando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origem]);

  useEffect(() => {
    if (!cobranca || !statusEfetivo || !DEVE_POLLAR.has(statusEfetivo)) return;

    let falhas = 0;

    function agendar() {
      // O teto é conferido a CADA ciclo, não só na entrada: sem isso, um erro
      // reagendava para sempre.
      const decorrido = Date.now() - inicioRef.current;
      if (decorrido > TETO_MS) { setTetoAtingido(true); return; }
      timerRef.current = setTimeout(consultar, decorrido < LIMIAR_LENTO_MS ? POLL_RAPIDO_MS : POLL_LENTO_MS);
    }

    function consultar() {
      if (Date.now() - inicioRef.current > TETO_MS) { setTetoAtingido(true); return; }
      origemRef.current!.consultar(cobranca!.txid)
        .then(dto => {
          falhas = 0;
          setCobranca(dto);
          if (dto.status === 'CONCLUIDA' || dto.status === 'CONCLUIDA_DIVERGENTE') { onDesfecho?.(); return; }
          if (!DEVE_POLLAR.has(dto.status)) return;   // CANCELADA, EXPIRADA, ERRO
          agendar();
        })
        .catch((e: any) => {
          falhas += 1;
          const codigo = e?.response?.data?.codigo;
          if (CODIGOS_TERMINAIS.has(codigo) || falhas >= MAX_FALHAS_SEGUIDAS) {
            setErro(mensagemDeErroPix(e));
            return;
          }
          agendar();
        });
    }

    const relogio = setInterval(() => {
      setRestanteMs(Math.max(0, TETO_MS - (Date.now() - inicioRef.current)));
    }, 1000);

    timerRef.current = setTimeout(consultar, POLL_RAPIDO_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); clearInterval(relogio); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cobranca?.txid, statusEfetivo]);

  /** Consulta agora — o app do banco não devolve o cooperado a esta tela. */
  const consultarAgora = () => {
    if (!cobranca || !origemRef.current) return;
    origemRef.current.consultar(cobranca.txid)
      .then(dto => {
        setCobranca(dto);
        if (dto.status === 'CONCLUIDA' || dto.status === 'CONCLUIDA_DIVERGENTE') onDesfecho?.();
      })
      .catch(e => setErro(mensagemDeErroPix(e)));
  };

  return { cobranca, statusEfetivo, criando, erro, tetoAtingido, restanteMs, consultarAgora };
}
